import { FC, useState } from 'react';
import { useSetRecoilState } from 'recoil';
import { DELETE_CONFIG_FILE, GET_CONFIG_FILES } from 'components/configurationFiles/queries';
import { ConfigurationFile } from 'components/configurationFiles/types';
import ConfirmDialog from 'components/common/Dialog/ConfirmDialog';
import DeleteIcon from '@mui/icons-material/Delete';
import ELEMENT from 'common/element';
import IconButton from 'components/common/IconButton/IconButton';
import Link from 'components/common/Link/Link';
import { pageAlertConfigState } from 'components/common/PageAlert/PageAlert.state';
import { PageAlertVariant } from 'components/common/PageAlert/PageAlert.types';
import { SIZE } from 'common/constants';
import Table from 'components/common/Table/Table';
import { useMutation } from '@apollo/client';
import { UserRowProp } from './types';

import './ConfigurationFilesList.scss';

type Props = {
  configurationFiles: ConfigurationFile[];
  onConfigDelete?: (id: string) => void;
};
type ConfirmationDialog = {
  active: boolean;
  for?: string;
};

interface ListItemProps extends ConfigurationFile {
  onDelete?: (id: string) => void;
}

function ListItem({
  id,
  name,
  readOnly,
  onDelete,
}: ListItemProps): JSX.Element {
  return (
    <div className="ConfigurationFilesList__Item" data-test-id={ELEMENT.configurationFilesListItem}>
      <Link href={`/file?path=${name}`} className="link">
        {name}
      </Link>
      {onDelete && (
        <IconButton
          className="ConfigurationFilesList__DeleteButton"
          tooltip="Delete file"
          onClick={(): void => onDelete(id)}
          disabled={readOnly}
          icon={<DeleteIcon />}
        />
      )}
    </div>
  );
}

const ConfigurationFilesList: FC<Props> = ({ configurationFiles }: Props) => {
  const setPageAlertConfig = useSetRecoilState(pageAlertConfigState);
  const [fileToDelete, setFileToDelete] = useState<string | null>(null);
  const showSuccessAlert = (): void => {
    setPageAlertConfig({
      message: 'File deleted',
      variant: PageAlertVariant.SUCCESS,
      autoHide: true,
      duration: 1.5,
    });
  };
  const [deleteConfigMutation] = useMutation(DELETE_CONFIG_FILE, {
    onCompleted: (): void => {
      showSuccessAlert();
    },
  });
  const [confirmationDialog, setConfirmationDialog] = useState<ConfirmationDialog>({ active: false });
  function handleConfigDeletion(id: string, name: string): void {
    setConfirmationDialog({ active: true, for: id });
    setFileToDelete(name);
  }
  function performConfigDeletion(): void {
    deleteConfigMutation({
      variables: {
        id: confirmationDialog.for,
      },
      refetchQueries: [{ query: GET_CONFIG_FILES }],
    });
  }
  function closeConfirmationDialog(): void {
    setConfirmationDialog({ active: false });
    setFileToDelete(null);
  }
  const HEADERS = [{ content: 'File', field: 'file' }];
  const tableData = configurationFiles.map(({ name, id, readOnly }) => {
    const file = (
      <ListItem
        key={id}
        id={id}
        readOnly={readOnly}
        name={name}
        onDelete={(): void => handleConfigDeletion(id, name)}
      />
    );
    return { file };
  });
  const tableOptions = {
    isStriped: false,
    isSortable: false,
    hoverRow: true,
    sidePadding: 10,
    size: SIZE.MEDIUM,
  };
  return (
    <div className="ConfigurationFilesList__Table" data-test-id={ELEMENT.configurationFilesListTable}>
      <Table
        headers={HEADERS}
        data={tableData}
        options={tableOptions}
        userRowProp={(): UserRowProp => ({ className: 'Table__Row ConfigurationFilesList__TableRow' })}
      />
      <ConfirmDialog
        title="Please Confirm"
        text={`Are you sure you want to delete the configuration file "${fileToDelete}"?`}
        show={confirmationDialog.active}
        close={closeConfirmationDialog}
        onSubmit={performConfigDeletion}
      />
    </div>
  );
};

export default ConfigurationFilesList;
